import React, { useState, useEffect } from 'react';
import { X, Plus, Loader2, Sparkles, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

/**
 * Create Task Modal - AI-Enhanced with Smart Estimates
 */
const CreateTaskModal = ({ caseId, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'MEDIUM',
    due_date: '',
    estimated_hours: '',
    assignee_user_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [showAISuggestion, setShowAISuggestion] = useState(false);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [baseEstimate, setBaseEstimate] = useState(null); // Store original AI estimate

  // Preset values
  const presets = [
    { label: 'Quick', value: 0.5, desc: '< 1 time' },
    { label: 'Short', value: 1, desc: '~1 time' },
    { label: 'Normal', value: 3, desc: '2-4 timer' },
    { label: 'Day', value: 8, desc: 'Full dag' },
    { label: 'Multiple', value: 16, desc: 'Flere dager' }
  ];

  // Fetch case members on mount
  useEffect(() => {
    fetchCaseMembers();
  }, [caseId]);

  // Fetch AI suggestion when title/description changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.title.trim().length > 5) {
        fetchAISuggestion();
      }
    }, 1000); // 1 second debounce

    return () => clearTimeout(timer);
  }, [formData.title, formData.description]);

  // Re-calculate estimate when assignee changes
  useEffect(() => {
    if (formData.assignee_user_id && baseEstimate && formData.title) {
      adjustEstimateForAssignee(formData.assignee_user_id);
    }
  }, [formData.assignee_user_id]);

  const fetchCaseMembers = async () => {
    setLoadingMembers(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/cases/${caseId}/members`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMembers(response.data.members || []);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchAISuggestion = async () => {
    if (!formData.title.trim()) return;

    setLoadingAI(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/ai/tasks/suggest-estimate`,
        {
          case_id: caseId,
          task_title: formData.title,
          task_description: formData.description || null,
          assignee_user_id: formData.assignee_user_id || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAiSuggestion(response.data);
      setBaseEstimate(response.data.estimated_hours); // Store base
      setShowAISuggestion(true);
      
      // Auto-apply if no estimate set yet
      if (!formData.estimated_hours) {
        setFormData(prev => ({
          ...prev,
          estimated_hours: response.data.estimated_hours
        }));
      }
    } catch (error) {
      console.error('AI suggestion error:', error);
      // Silent fail - don't bother user
    } finally {
      setLoadingAI(false);
    }
  };

  const adjustEstimateForAssignee = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/ai/tasks/suggest-estimate`,
        {
          case_id: caseId,
          task_title: formData.title,
          task_description: formData.description || null,
          assignee_user_id: userId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const newEstimate = response.data.estimated_hours;
      const oldEstimate = parseFloat(formData.estimated_hours) || baseEstimate;

      if (newEstimate !== oldEstimate) {
        setFormData(prev => ({
          ...prev,
          estimated_hours: newEstimate
        }));

        // Show toast notification
        const member = members.find(m => m.user_id === userId);
        if (newEstimate > oldEstimate) {
          toast.info(`\u23f1\ufe0f Estimat justert til ${newEstimate}t for ${member?.user_name || 'valgt person'}`);
        } else {
          toast.success(`\u2705 Estimat redusert til ${newEstimate}t for ${member?.user_name || 'valgt person'}`);
        }
      }
    } catch (error) {
      console.error('Adjust estimate error:', error);
    }
  };

  const applyPreset = (value) => {
    setFormData({ ...formData, estimated_hours: value });
    setShowAISuggestion(false);
  };

  const applyAISuggestion = () => {
    if (aiSuggestion) {
      setFormData({
        ...formData,
        estimated_hours: aiSuggestion.estimated_hours
      });
      setShowAISuggestion(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Tittel er påkrevd');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        title: formData.title,
        description: formData.description || null,
        priority: formData.priority,
        source: 'MANUAL',
        due_date: formData.due_date || null,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
        assignee_user_id: formData.assignee_user_id || null
      };

      await axios.post(
        `${API}/tasks/?case_id=${caseId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Oppgave opprettet!');
      onCreated();
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Kunne ikke opprette oppgave');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-blue-50">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Plus className="w-6 h-6 text-indigo-600" />
              Ny oppgave
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Tittel *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-indigo-500"
              placeholder="F.eks. Gjennomgå kontrakt"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Beskrivelse
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-indigo-500"
              rows={3}
              placeholder="Detaljer om oppgaven..."
            />
          </div>

          {/* Assignee */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Tildel til (valgfritt)
            </label>
            {loadingMembers ? (
              <div className="text-sm text-gray-500">Laster teammedlemmer...</div>
            ) : (
              <select
                value={formData.assignee_user_id}
                onChange={(e) => setFormData({ ...formData, assignee_user_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-indigo-500"
              >
                <option value="">Ikke tildelt ennå</option>
                {members.map((member) => (
                  <option key={member.user_id} value={member.user_id}>
                    {member.user_name} ({member.role})
                  </option>
                ))}
              </select>
            )}
            {formData.assignee_user_id && (
              <p className="text-xs text-gray-500 mt-1">
                💡 Estimatet justeres automatisk basert på valgt person
              </p>
            )}
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Prioritet
            </label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-indigo-500"
            >
              <option value="LOW">Lav</option>
              <option value="MEDIUM">Middels</option>
              <option value="HIGH">Høy</option>
              <option value="URGENT">Haster</option>
            </select>
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Forfallsdato
            </label>
            <input
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value ? e.target.value + 'T12:00:00Z' : '' })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-indigo-500"
            />
          </div>

          {/* Estimated Hours with AI */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              Estimerte timer
              {loadingAI && <Loader2 className="w-4 h-4 animate-spin text-gray-600" />}
            </label>

            {/* AI Suggestion Banner */}
            {showAISuggestion && aiSuggestion && (
              <div className="mb-3 p-3 bg-gray-50 border border-indigo-200 rounded-lg">
                <div className="flex items-start gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-indigo-900 mb-1">
                      AI foreslår: {aiSuggestion.estimated_hours}t 
                      <span className="ml-2 text-xs font-normal text-indigo-700">
                        ({presets.find(p => p.value === aiSuggestion.estimated_hours)?.label || aiSuggestion.preset})
                      </span>
                    </p>
                    <div className="text-xs text-indigo-700 space-y-0.5">
                      {aiSuggestion.reasoning.map((reason, i) => (
                        <div key={i}>• {reason}</div>
                      ))}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAISuggestion(false)}
                    className="text-indigo-400 hover:text-indigo-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {formData.estimated_hours !== aiSuggestion.estimated_hours && (
                  <button
                    type="button"
                    onClick={applyAISuggestion}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    → Bruk dette estimatet
                  </button>
                )}
              </div>
            )}

            {/* Preset Buttons */}
            <div className="grid grid-cols-5 gap-2 mb-3">
              {presets.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => applyPreset(preset.value)}
                  className={`p-2 rounded-lg border-2 text-center transition-all ${
                    parseFloat(formData.estimated_hours) === preset.value
                      ? 'border-indigo-600 bg-gray-50 text-indigo-900'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  <div className="text-xs font-semibold">{preset.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{preset.value}t</div>
                </button>
              ))}
            </div>

            {/* Manual Input */}
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-400" />
              <input
                type="number"
                step="0.5"
                min="0"
                value={formData.estimated_hours}
                onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value })}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-indigo-500"
                placeholder="Eller skriv egendefinert..."
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Oppretter...
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  Opprett oppgave
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors"
            >
              Avbryt
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTaskModal;
